import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useUpdateBooking } from "../../../../../api/fleet-management/bookings/booking.hooks";
import { useGetDrivers } from "../../../../../api/fleet-management/drivers/drivers.hooks";

const initialForm = {
  driverAssignments: [],
};

const createAssignmentRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const getDriverLabel = (driver) => {
  if (!driver) return "Unnamed Driver";
  if (typeof driver === "string") return driver;

  return (
    driver?.name ||
    [driver?.firstName, driver?.lastName].filter(Boolean).join(" ") ||
    driver?.contactNumber ||
    getIdValue(driver)
  );
};

const getBookingVehicles = (booking) => {
  if (Array.isArray(booking?.vehicles) && booking.vehicles.length > 0) {
    return booking.vehicles
      .map((vehicle, index) => {
        const vehicleId = getIdValue(vehicle?.vehicleId);

        return {
          bookingVehicleKey: `${vehicleId || "vehicle"}-${index}`,
          vehicleId,
          label:
            vehicle?.snapshot?.name ||
            vehicle?.vehicleId?.name ||
            getIdValue(vehicle?.vehicleId) ||
            "Vehicle",
        };
      })
      .filter((vehicle) => Boolean(vehicle.vehicleId));
  }

  if (booking?.vehicleId) {
    const normalizedVehicleId = getIdValue(booking.vehicleId);
    return [
      {
        bookingVehicleKey: `${normalizedVehicleId || "vehicle"}-0`,
        vehicleId: normalizedVehicleId,
        label:
          [booking?.vehicleSnapshot?.make, booking?.vehicleSnapshot?.model].filter(Boolean).join(" ") ||
          [booking?.vehicleId?.make, booking?.vehicleId?.model].filter(Boolean).join(" ") ||
          normalizedVehicleId,
      },
    ].filter((vehicle) => Boolean(vehicle.vehicleId));
  }

  return [];
};

const EditDriverAssignmentsModal = ({ show, onClose, booking, onSuccess, onError }) => {
  const { mutate: updateBooking, isLoading: isSubmitting } = useUpdateBooking();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const bookingVehicles = useMemo(() => getBookingVehicles(booking), [booking]);
  const bookingVehiclesKey = useMemo(
    () => bookingVehicles.map((vehicle) => vehicle.bookingVehicleKey).join("|"),
    [bookingVehicles]
  );

  useEffect(() => {
    if (!show) return;
    const assignmentsByVehicleKey = new Map();

    (Array.isArray(booking?.driverAssignments) ? booking.driverAssignments : []).forEach((assignment) => {
      const vehicleId = getIdValue(assignment?.vehicleId);
      const bookingVehicleKey = assignment?.bookingVehicleKey || `${vehicleId || "vehicle"}-0`;

      if (!assignmentsByVehicleKey.has(bookingVehicleKey)) {
        assignmentsByVehicleKey.set(bookingVehicleKey, []);
      }

      assignmentsByVehicleKey.get(bookingVehicleKey).push({
        rowId: createAssignmentRowId(),
        bookingVehicleKey,
        vehicleId,
        driverId: getIdValue(assignment?.driverId),
      });
    });

    const rows = bookingVehicles.flatMap((vehicle) => {
      const existingRows = assignmentsByVehicleKey.get(vehicle.bookingVehicleKey) || [];

      if (existingRows.length > 0) {
        return existingRows;
      }

      return [
        {
          rowId: createAssignmentRowId(),
          bookingVehicleKey: vehicle.bookingVehicleKey,
          vehicleId: vehicle.vehicleId,
          driverId: "",
        },
      ];
    });

    setForm({ driverAssignments: rows });
    setErrors({});
    setFeedback({ type: "", message: "" });
  }, [booking?._id, bookingVehiclesKey, show]);

  const { data: driversData, isLoading: isLoadingDrivers } = useGetDrivers();
  const availableDrivers = Array.isArray(driversData) ? driversData : driversData?.drivers || driversData?.items || [];

  const driverAssignments = form?.driverAssignments || [];

  const allVehiclesAssigned = bookingVehicles.every((vehicle) => {
    const assignedForVehicle = driverAssignments.filter(
      (assignment) => assignment?.bookingVehicleKey === vehicle.bookingVehicleKey
    );
    if (assignedForVehicle.length === 0) return false;
    return assignedForVehicle.every((assignment) => Boolean(assignment?.driverId));
  });

  const updateVehicleDriverAssignment = (rowId, driverId) => {
    setForm((prev) => ({
      ...prev,
      driverAssignments: prev.driverAssignments.map((assignment) =>
        assignment.rowId === rowId ? { ...assignment, driverId } : assignment
      ),
    }));
    setErrors((prev) => ({ ...prev, driverAssignments: undefined }));
  };

  const addVehicleDriverAssignment = (bookingVehicleKey) => {
    setForm((prev) => ({
      ...prev,
      driverAssignments: [
        ...prev.driverAssignments,
        {
          rowId: createAssignmentRowId(),
          bookingVehicleKey,
          vehicleId: bookingVehicles.find((v) => v.bookingVehicleKey === bookingVehicleKey)?.vehicleId || "",
          driverId: "",
        },
      ],
    }));
    setErrors((prev) => ({ ...prev, driverAssignments: undefined }));
  };

  const removeVehicleDriverAssignment = (rowId) => {
    setForm((prev) => ({
      ...prev,
      driverAssignments: prev.driverAssignments.filter((assignment) => assignment.rowId !== rowId),
    }));
    setErrors((prev) => ({ ...prev, driverAssignments: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!allVehiclesAssigned || form.driverAssignments.length === 0) {
      nextErrors.driverAssignments = "Assign drivers for all vehicles.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!booking?._id || !validate()) return;

    const payload = {
      driverAssignments: form.driverAssignments.map((assignment) => ({
        vehicleId: getIdValue(assignment.vehicleId),
        driverId: assignment.driverId,
      })),
    };

    updateBooking(
      { bookingId: booking._id, bookingData: payload },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Assignments updated." });
          onSuccess?.();
          onClose?.();
        },
        onError: (err) => {
          setFeedback({ type: "danger", message: err?.response?.data?.message || "Failed to update assignments." });
          onError?.(err?.response?.data?.message || "Failed to update assignments.");
        },
      }
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered key={booking?._id || "edit-assignments-modal"}>
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Edit Driver Assignments</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {feedback.message && (
          <Alert variant={feedback.type} className="mb-3">
            {feedback.message}
          </Alert>
        )}

        <p className="mb-3 text-muted">
          Edit driver assignments for <strong>{booking?.customerSnapshot?.name || booking?.fullName || "customer"}</strong>.
        </p>

        {!!errors.driverAssignments && (
          <div className="text-danger small mb-2">{errors.driverAssignments}</div>
        )}

        {(bookingVehicles || []).map((vehicle, index) => {
          const assignmentsForVehicle = driverAssignments.filter(
            (item) => item.bookingVehicleKey === vehicle.bookingVehicleKey
          );

          return (
            <div className="mb-4" key={vehicle.bookingVehicleKey || vehicle.vehicleId || index}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <Form.Label className="mb-1 d-block">
                    Assign Driver for {vehicle.label || `Vehicle ${index + 1}`}
                  </Form.Label>
                  <div className="text-muted small">
                    {vehicle.label}
                    {bookingVehicles.length > 1 ? ` - Vehicle ${index + 1}` : ""}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  onClick={() => addVehicleDriverAssignment(vehicle.bookingVehicleKey)}
                  disabled={isLoadingDrivers || isSubmitting}
                >
                  Add Driver
                </Button>
              </div>

              {assignmentsForVehicle.map((assignment) => {
                const selectedDriverIdsInOtherRows = new Set(
                  driverAssignments
                    .filter(
                      (item) =>
                        item?.bookingVehicleKey === assignment.bookingVehicleKey &&
                        item?.rowId !== assignment.rowId &&
                        Boolean(item?.driverId)
                    )
                    .map((item) => item.driverId)
                );

                const driverOptions = availableDrivers.filter((driver) => {
                  const driverId = driver?._id;
                  if (!driverId) return false;
                  if (assignment?.driverId === driverId) return true;
                  return !selectedDriverIdsInOtherRows.has(driverId);
                });

                return (
                  <div className="d-flex align-items-start gap-2 mb-2" key={assignment.rowId}>
                    <Form.Select
                      value={assignment?.driverId || ""}
                      onChange={(e) => updateVehicleDriverAssignment(assignment.rowId, e.target.value)}
                      disabled={isLoadingDrivers || isSubmitting}
                    >
                      <option value="">Select available driver</option>
                      {driverOptions.map((driver) => (
                        <option key={driver?._id} value={driver?._id}>
                          {getDriverLabel(driver)} {driver?.contactNumber ? `(${driver.contactNumber})` : ""}
                        </option>
                      ))}
                    </Form.Select>

                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeVehicleDriverAssignment(assignment.rowId)}
                      disabled={isLoadingDrivers || isSubmitting || assignmentsForVehicle.length === 1}
                      title="Remove driver assignment"
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}

              {assignmentsForVehicle.length === 0 && (
                <div className="text-danger small">Add at least one driver assignment for this vehicle.</div>
              )}
            </div>
          );
        })}

      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSubmitting || !allVehiclesAssigned}
        >
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditDriverAssignmentsModal;
